import styled, { css } from "styled-components";
import {
  NEW_LOOK_CARDS_VIEW_MAX_WIDTH,
  NEW_LOOK_PHONE_MAX_WIDTH,
  NEW_LOOK_SECTION_GAP,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import {
  getNewLookTokens,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";

/** Page body under the new-look page intro. */
export const NewLookPageBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
  min-width: 0;
  @media (max-width: 700px) {
    padding: 12px 16px 16px;
    gap: 12px;
  }
`;

/** Top row of a portfolio page: a wide chart next to the allocation card. */
export const NewLookOverviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
  gap: 16px;
  align-items: stretch;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** Filters chips on the left, list controls on the right. */
export const NewLookToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 36px;
  margin-bottom: 12px;
`;

export const NewLookToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  &:last-child {
    margin-left: auto;
  }
`;

/** Card title inside a Surface (the panel title role). */
export const NewLookCardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;

/** Responsive cards grid; the content defines the height. */
export const NewLookCardsGrid = styled.div<{
  $minWidth?: number;
  $tabletMinWidth?: number;
}>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${(p) => p.$minWidth ?? 300}px), 1fr)
  );
  gap: 16px;
  ${(p) =>
    p.$tabletMinWidth &&
    css`
      @media (max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH}) {
        grid-template-columns: repeat(
          auto-fill,
          minmax(min(100%, ${p.$tabletMinWidth}px), 1fr)
        );
      }
    `}
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    gap: 8px;
  }
`;

/** Spacing under a new-look card that tops a mobile list. */
export const NewLookMobileTop = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

/**
 * Phone card lists: the shared column sorters become a quiet caption row
 * (no tinted bar) lined up with the cards' inner padding.
 */
export const NewLookSortersRow = styled.div<{
  /** Sorter row padding that lines the captions up with the rows' columns. */
  $padding?: string;
  /** Right-aligns every caption after the first (numeric columns). */
  $endAligned?: boolean;
}>`
  /* !important: some lists pass the sorters an inline 12px padding; the
     captions must start on the cards' 14px content edge. */
  > div {
    background: transparent;
    border-radius: 0;
    padding: ${(p) => p.$padding ?? "4px 14px"} !important;
  }
  ${(p) =>
    p.$endAligned &&
    css`
      .ant-row > .ant-col:not(:first-child) {
        justify-content: flex-end;
      }
    `}
  .ant-row > div > div {
    font-size: 12.5px;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
    line-height: 1.3;
  }
  .anticon-caret-right {
    font-size: 9px;
  }
`;

/** A light phone list row: soft shadow (hairline in dark), 12px radius. */
export const newLookListCard = css`
  border-radius: 12px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;

/** Clickable card depth: soft shadow, lifts a little on hover. */
export const newLookLiftCard = css`
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  transition: border-color 150ms, box-shadow 150ms, transform 150ms;
  &:hover {
    box-shadow: ${(p) =>
      p.theme.isDarkMode
        ? `inset 0 0 0 1px ${getNewLookTokens(p.theme).hairlineStrong}`
        : p.theme.shadows.hoverCard};
    transform: translateY(-1px);
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
`;

/** Drawer tab body: the allocation card, then a titled list section. */
export const NewLookDrawerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/**
 * An antd table edge to edge in a flush panel: the outer cells line up with
 * the panel title, rows are hoverable and the pagination sits inside.
 */
export const NewLookPanelTable = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    padding-left: 24px;
  }
  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    padding-right: 24px;
  }
  .ant-table-tbody > tr.ant-table-row > td {
    height: 60px;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  .ant-table-tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: 12px;
  }
  .ant-table-tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: 12px;
  }
  .ant-table-wrapper .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 12px 24px;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
